import { Component, OnInit } from '@angular/core';
import { AdminService } from '../../../service/admin.service';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { UploadService } from '../../../service/upload.service';
import { ImageUrlPipe } from '../../../pipes/image-url.pipe';

interface RiskLevel {
  label: string;
  value: string;
}

@Component({
  selector: 'app-portfolios-management-tab',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideIconsModule, PrimengImports, ConfirmDialogModule, DropdownModule, FileUploadModule,ImageUrlPipe],
  templateUrl: './portfolios-management-tab.component.html',
  styleUrl: './portfolios-management-tab.component.css',
  providers: [MessageService, ConfirmationService]
})
export class PortfoliosManagementTabComponent implements OnInit {
  portfolios: any[] = [];
  // isLoading = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  };

  showAddDialog = false;
  addPortfolioForm!: FormGroup;
  addLoading = false;
  editingPortfolioId: string | null = null;
  uploadedImages: File[] = [];
  imagePreviews: string[] = [];
  imagesToDelete: string[] = [];
  // Collected S3 keys after successful upload
  uploadedImageKeys: string[] = [];
  // Optional simple status per file
  fileStatus: { [name: string]: 'pending' | 'uploading' | 'done' | 'failed' } = {};

  riskLevels: RiskLevel[] = [
    { label: 'Low', value: 'Low' },
    { label: 'Medium', value: 'Medium' },
    // { label: 'High', value: 'High' }
  ];
isLoading: boolean = false;
  showPublishDialog = false;
  publishLoading = false;
  publishPortfolioId: string | null = null;
  publishForm!: FormGroup;
  constructor(
    private adminService: AdminService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private uploadService: UploadService
  ) {
    this.initAddPortfolioForm();
  }

  ngOnInit(): void {
    this.fetchPortfolios(1);
      this.publishForm = this.fb.group({
      actualRoiPercentage: [null, [Validators.required, Validators.min(0.01)]]
    });
  }

  fetchPortfolios(page: number) {
    this.isLoading = true;
    this.adminService.getPortfolios(page, this.pagination.itemsPerPage).subscribe({
      next: (data) => {
        this.portfolios = data.portfolios;
        this.pagination = data.pagination;
        this.isLoading = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to fetch portfolios.', life: 3000 });
        this.isLoading = false;
      }
    });
  }

  onPageChange(event: any) {
    this.fetchPortfolios(event.page + 1);
  }

  openAddDialog(portfolio: any = null) {
    this.showAddDialog = true;
    this.addPortfolioForm.reset();
    this.editingPortfolioId = null;
    this.uploadedImages = [];
    this.imagePreviews = [];
    this.imagesToDelete = [];
    
    if (portfolio) {
      this.editingPortfolioId = portfolio._id;
      const formValue = {
        ...portfolio,
        investmentFocus: Array.isArray(portfolio.investmentFocus) ? portfolio.investmentFocus.join(', ') : portfolio.investmentFocus,
        investmentStartDate: portfolio.investmentStartDate ? new Date(portfolio.investmentStartDate) : null,
        investmentClosureDate: portfolio.investmentClosureDate ? new Date(portfolio.investmentClosureDate) : null,
        isActive: portfolio.isActive !== undefined ? portfolio.isActive : true
      };
      this.addPortfolioForm.patchValue(formValue);
      
      if (portfolio.images && portfolio.images.length) {
        this.imagePreviews = [...portfolio.images];
      }
    } else {
      this.addPortfolioForm.patchValue({
        riskLevel: 'Low',
        isActive: true
      });
    }
  }

  closeAddDialog() {
    this.showAddDialog = false;
    this.addPortfolioForm.reset();
    this.uploadedImages = [];
    this.imagePreviews = [];
    this.imagesToDelete = [];
    this.addLoading = false;
  }

  initAddPortfolioForm() {
    this.addPortfolioForm = this.fb.group({
      name: ['', Validators.required],
      riskLevel: ['Low', Validators.required],
      description: [null],
      roiPotential: [null, [Validators.required, Validators.min(1)]],
      tenure: [null, [Validators.required, Validators.min(1)]],
      ticketPrice: [null, [Validators.required, Validators.min(1)]],
      totalTickets: [null, [Validators.required, Validators.min(1)]],
      investmentFocus: ['', Validators.required],
      investmentStartDate: [null, Validators.required],
      investmentClosureDate: [null, Validators.required],
      adminNote: [''],
      isActive: [true]
    });
  }

  onImagesUpload(event: any) {
    const files = event.files;
    this.handleSelectedFiles(files);
  }

  onImagesSelected(event: any) {
    const files = Array.isArray(event.files) ? event.files : Array.from(event.files || []);
    if (files && files.length) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      const validFiles = files.filter((file: File) => {
        if (!allowedTypes.includes(file.type)) {
          this.messageService.add({ severity: 'error', summary: 'Invalid Format', detail: 'Only PNG, JPG, and JPEG formats are allowed.', life: 4000 });
          return false;
        }
        if (file.size > maxSize) {
          this.messageService.add({ severity: 'error', summary: 'File Too Large', detail: 'Each image must be less than 2MB.', life: 4000 });
          return false;
        }
        return true;
      });
      if (validFiles.length) {
        this.handleSelectedFiles(validFiles);
      }
    }
}

  removeImage(index: number) {
    const imageUrl = this.imagePreviews[index];
    if (this.editingPortfolioId && !imageUrl.startsWith('data:')) {
      this.imagesToDelete.push(imageUrl);
    }
    this.imagePreviews.splice(index, 1);
    // Remove uploaded image only if it is a new upload (objectUrl)
    if (imageUrl.startsWith('blob:')) {
      const fileIndex = this.uploadedImages.findIndex(f => {
        const url = this.imagePreviews[index];
        return url && url.startsWith('blob:');
      });
      if (fileIndex !== -1) {
        this.uploadedImages.splice(fileIndex, 1);
        this.uploadedImageKeys.splice(fileIndex, 1);
      }
    }
  }

  private async handleSelectedFiles(files: File[]) {
    for (const file of files) {
      const objectUrl = URL.createObjectURL(file);
      this.fileStatus[file.name] = 'pending';
      this.fileStatus[file.name] = 'uploading';
      this.uploadService.getPresign(file).subscribe({
        next: (presign) => {
          if (!presign || !presign.uploadUrl || !presign.key) {
            this.fileStatus[file.name] = 'failed';
            this.messageService.add({ severity: 'error', summary: 'Upload Error', detail: 'Invalid presign response', life: 4000 });
            return;
          }
          this.uploadService.putToS3(presign.uploadUrl, file)
            .then(() => {
              this.uploadedImageKeys.push(presign.key);
              this.fileStatus[file.name] = 'done';
              // Only add preview and image if upload succeeded
              this.uploadedImages.push(file);
              this.imagePreviews.push(objectUrl);
            })
            .catch((e: any) => {
              this.fileStatus[file.name] = 'failed';
              this.messageService.add({ severity: 'error', summary: 'Upload Error', detail: e?.message || `Failed to upload ${file.name}` , life: 4000 });
            });
        },
        error: (err) => {
          this.fileStatus[file.name] = 'failed';
          this.messageService.add({ severity: 'error', summary: 'Presign Error', detail: err?.message || `Failed to presign ${file.name}`, life: 4000 });
        },
        complete: () => {}
      });
    }
  }

  async submitAddPortfolio() {
    if (this.addPortfolioForm.invalid) {
      Object.keys(this.addPortfolioForm.controls).forEach(key => {
        const control = this.addPortfolioForm.get(key);
        if (control?.invalid) {
          control.markAsTouched();
        }
      });
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields correctly.', life: 3000 });
      return;
    }

    this.addLoading = true;
    const formValue = { ...this.addPortfolioForm.value };
    
    // Handle investment focus array
    formValue.investmentFocus = formValue.investmentFocus
      .split(',')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);

    // Convert dates to ISO strings
    if (formValue.investmentStartDate instanceof Date) {
      formValue.investmentStartDate = formValue.investmentStartDate.toISOString();
    }
    if (formValue.investmentClosureDate instanceof Date) {
      formValue.investmentClosureDate = formValue.investmentClosureDate.toISOString();
    }
    // Block submit if any files failed or are still uploading
    const hasPending = Object.values(this.fileStatus).some(s => s === 'uploading' || s === 'pending');
    const hasFailed = Object.values(this.fileStatus).some(s => s === 'failed');
    if (hasPending) {
      this.addLoading = false;
      this.messageService.add({ severity: 'warn', summary: 'Please wait', detail: 'Uploads in progress. Try again after uploads complete.', life: 3000 });
      return;
    }
    if (hasFailed) {
      this.addLoading = false;
      this.messageService.add({ severity: 'error', summary: 'Upload Error', detail: 'One or more image uploads failed. Please fix and retry.', life: 4000 });
      return;
    }

    if (this.editingPortfolioId) {
      // Minimal change: keep current update flow unless backend requires keys.
      const payload = {
        ...formValue,
        imagesToAdd: this.uploadedImageKeys,
        imagesToDelete: this.imagesToDelete
      };
      this.adminService.updatePortfolio(this.editingPortfolioId, payload).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Portfolio updated successfully!', life: 3000 });
          this.closeAddDialog();
          this.fetchPortfolios(this.pagination.currentPage);
        },
        error: (err) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to update portfolio.', life: 3000 });
        },
        complete: () => {
          this.addLoading = false;
        }
      });
    } else {
      const payload = {
        ...formValue,
        images: this.uploadedImageKeys
      };
      this.adminService.createPortfolio(payload).subscribe({
        next: () => {
          this.addLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Portfolio added successfully!', life: 3000 });
          this.closeAddDialog();
          this.fetchPortfolios(this.pagination.currentPage);
        },
        error: (err) => {
          this.addLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to add portfolio.', life: 3000 });
        },
        complete: () => {
          this.addLoading = false;
        }
      });
    }
  }

  onEditPortfolio(portfolio: any) {
    this.openAddDialog(portfolio);
  }

  onDeletePortfolio(portfolio: any) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${portfolio.name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.adminService.deletePortfolio(portfolio._id).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Portfolio deleted successfully!', life: 3000 });
            this.fetchPortfolios(this.pagination.currentPage);
          },
          error: (err) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to delete portfolio.', life: 3000 });
          }
        });
      }
    });
  }
  
  openPublishDialog(portfolio: any) {
    this.publishPortfolioId = portfolio._id;
    this.publishForm.reset();
    this.showPublishDialog = true;
  }

  closePublishDialog() {
    this.showPublishDialog = false;
    this.publishPortfolioId = null;
    this.publishForm.reset();
  }

  submitPublishPortfolio() {
    if (this.publishForm.invalid || !this.publishPortfolioId) {
      this.publishForm.markAllAsTouched();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please enter a valid Actual ROI Percentage.', life: 3000 });
      return;
    }
    this.publishLoading = true;
    const actualRoiPercentage = this.publishForm.value.actualRoiPercentage;
    this.adminService.publishPortfolio(this.publishPortfolioId, actualRoiPercentage).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Portfolio published successfully!', life: 3000 });
        this.closePublishDialog();
        this.fetchPortfolios(this.pagination.currentPage);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to publish portfolio.', life: 3000 });
      },
      complete: () => {
        this.publishLoading = false;
      }
    });
  }
    formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

}
